import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  BellIcon,
  EnvelopeIcon,
  ComputerDesktopIcon,
  MoonIcon,
  ClockIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  BriefcaseIcon,
  CalendarDaysIcon,
  SpeakerWaveIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Loader2 } from 'lucide-react';
import { API_BASE_URL } from '../../api/axiosConfig';

const API = API_BASE_URL;

// ============================================================
// Reusable Toggle Switch
// ============================================================
const Toggle = ({ checked, onChange, disabled = false, size = 'md' }) => {
  const sizes = {
    md: { track: 'h-6 w-11', thumb: 'h-5 w-5', on: 'translate-x-5', off: 'translate-x-0' },
    sm: { track: 'h-5 w-9',  thumb: 'h-4 w-4', on: 'translate-x-4', off: 'translate-x-0' },
  };
  const s = sizes[size] || sizes.md;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`
        relative inline-flex flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
        transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2
        focus-visible:ring-blue-500 focus-visible:ring-offset-2
        ${s.track}
        ${disabled
          ? 'opacity-40 cursor-not-allowed bg-gray-300 dark:bg-gray-600'
          : checked
            ? 'bg-blue-600'
            : 'bg-gray-300 dark:bg-gray-600'
        }
      `}
    >
      <span className="sr-only">{checked ? 'Enabled' : 'Disabled'}</span>
      <span
        aria-hidden="true"
        className={`
          pointer-events-none inline-block transform rounded-full bg-white shadow ring-0
          transition duration-200 ease-in-out
          ${s.thumb}
          ${checked ? s.on : s.off}
        `}
      />
    </button>
  );
};

// ============================================================
// Category row data
// ============================================================
const CATEGORIES = [
  {
    key: 'shifts',
    label: 'Shift Assignments',
    description: 'When a shift is assigned to you or updated.',
    icon: CalendarDaysIcon,
    color: 'text-violet-600',
    bg: 'bg-violet-50 dark:bg-violet-900/30',
  },
  {
    key: 'allocations',
    label: 'Project Allocations',
    description: 'When you are allocated to or removed from a project.',
    icon: BriefcaseIcon,
    color: 'text-blue-600',
    bg: 'bg-blue-50 dark:bg-blue-900/30',
  },
  {
    key: 'leave',
    label: 'Leave Updates',
    description: 'When your leave request is approved or rejected.',
    icon: ClockIcon,
    color: 'text-amber-600',
    bg: 'bg-amber-50 dark:bg-amber-900/30',
  },
  {
    key: 'onboarding',
    label: 'Account & Onboarding',
    description: 'Account creation, onboarding steps, and role changes.',
    icon: UserGroupIcon,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50 dark:bg-emerald-900/30',
  },
  {
    key: 'campaigns',
    label: 'Broadcast Messages',
    description: 'Announcements and mass messages from admins.',
    icon: SpeakerWaveIcon,
    color: 'text-pink-600',
    bg: 'bg-pink-50 dark:bg-pink-900/30',
  },
  {
    key: 'system',
    label: 'System Alerts',
    description: 'Critical system-level events and security notices.',
    icon: ShieldCheckIcon,
    color: 'text-red-600',
    bg: 'bg-red-50 dark:bg-red-900/30',
  },
];

// Default prefs (mirrors backend DEFAULT_PREFS)
const DEFAULT_PREFS = {
  shifts_inapp: true,       allocations_inapp: true,
  leave_inapp: true,        onboarding_inapp: true,
  campaigns_inapp: true,    system_inapp: true,
  shifts_email: true,       allocations_email: true,
  leave_email: true,        onboarding_email: true,
  campaigns_email: true,    system_email: true,
  all_inapp_enabled: true,
  all_email_enabled: true,
  desktop_enabled: true,
  quiet_hours_enabled: false,
  quiet_start: '22:00',
  quiet_end: '08:00',
};

// ============================================================
// Main Page
// ============================================================
export default function NotificationSettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [prefs, setPrefs]         = useState(DEFAULT_PREFS);
  const [loading, setLoading]     = useState(true);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [desktopPermission, setDesktopPermission] = useState('unsupported');

  const debounceRef = useRef(null);
  const latestPrefs = useRef(prefs);
  latestPrefs.current = prefs;

  // ── Check browser notification permission ──────────────────
  useEffect(() => {
    if (!('Notification' in window)) {
      setDesktopPermission('unsupported');
    } else {
      setDesktopPermission(Notification.permission); // 'default' | 'granted' | 'denied'
    }
  }, []);

  // ── Fetch preferences on mount ─────────────────────────────
  useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/notifications/preferences`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();

        // Normalise TIME values from DB '22:00:00' → '22:00'
        setPrefs({
          ...DEFAULT_PREFS,
          ...data,
          quiet_start: (data.quiet_start || '22:00:00').slice(0, 5),
          quiet_end:   (data.quiet_end   || '08:00:00').slice(0, 5),
        });
      } catch {
        // Fall back to defaults silently
      } finally {
        setLoading(false);
      }
    };
    fetchPrefs();
  }, []);

  // ── Auto-save with 500 ms debounce ─────────────────────────
  const scheduleSave = useCallback(() => {
    setSaveState('saving');
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/notifications/preferences`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(latestPrefs.current),
        });
        if (!res.ok) throw new Error('Save failed');
        setSaveState('saved');
        setTimeout(() => setSaveState('idle'), 2500);
      } catch {
        setSaveState('error');
        setTimeout(() => setSaveState('idle'), 3000);
      }
    }, 500);
  }, []);

  // ── Generic setter ─────────────────────────────────────────
  const update = useCallback((patch) => {
    setPrefs((prev) => {
      const next = { ...prev, ...patch };
      latestPrefs.current = next;
      return next;
    });
    scheduleSave();
  }, [scheduleSave]);

  // ── Request desktop permission ─────────────────────────────
  const requestDesktopPermission = async () => {
    if (!('Notification' in window)) return;
    const result = await Notification.requestPermission();
    setDesktopPermission(result);
    if (result === 'granted') {
      update({ desktop_enabled: true });
    }
  };

  // ── Save indicator label ───────────────────────────────────
  const SaveIndicator = () => {
    if (saveState === 'idle') return null;
    if (saveState === 'saving')
      return (
        <span className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…
        </span>
      );
    if (saveState === 'saved')
      return (
        <span className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
          <CheckCircleIcon className="w-4 h-4" /> Saved
        </span>
      );
    return (
      <span className="flex items-center gap-1.5 text-xs text-red-500">
        <ExclamationTriangleIcon className="w-4 h-4" /> Save failed — retrying…
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto font-sans">

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-xl font-extrabold text-gray-900 dark:text-white flex items-center gap-2">
            <BellIcon className="w-6 h-6 text-blue-600" />
            Notification Preferences
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Control which notifications you receive and how you receive them.
          </p>
        </div>
        <SaveIndicator />
      </div>

      {/* ── Section 1: Master Controls ─────────────────────── */}
      <section className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">
          Master Controls
        </h2>

        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {/* In-App master */}
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600">
                <BellIcon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  In-App Notifications
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Notifications in the inbox and real-time popups
                </p>
              </div>
            </div>
            <Toggle
              checked={prefs.all_inapp_enabled}
              onChange={(v) => update({ all_inapp_enabled: v })}
            />
          </div>

          {/* Email master */}
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-violet-50 dark:bg-violet-900/30 text-violet-600">
                <EnvelopeIcon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  Email Notifications
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Notifications delivered to your email address
                </p>
              </div>
            </div>
            <Toggle
              checked={prefs.all_email_enabled}
              onChange={(v) => update({ all_email_enabled: v })}
            />
          </div>
        </div>
      </section>

      {/* ── Section 2: Category Preferences ───────────────── */}
      <section className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">
          Notification Categories
        </h2>
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-5">
          Fine-tune which events notify you. Master controls above override these.
        </p>

        {/* Column headers */}
        <div className="hidden sm:grid grid-cols-[1fr_80px_80px] gap-2 px-1 mb-2">
          <span />
          <span className="text-center text-xs font-bold text-gray-400 dark:text-gray-500 uppercase">
            In-App
          </span>
          <span className="text-center text-xs font-bold text-gray-400 dark:text-gray-500 uppercase">
            Email
          </span>
        </div>

        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {CATEGORIES.map(({ key, label, description, icon: Icon, color, bg }) => {
            const inAppKey  = `${key}_inapp`;
            const emailKey  = `${key}_email`;
            const inAppDisabled = !prefs.all_inapp_enabled;
            const emailDisabled = !prefs.all_email_enabled;

            return (
              <div
                key={key}
                className="grid grid-cols-[1fr_auto_auto] sm:grid-cols-[1fr_80px_80px] items-center gap-3 py-4"
              >
                {/* Icon + text */}
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`flex-shrink-0 p-2 rounded-lg ${bg} ${color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {label}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                      {description}
                    </p>
                  </div>
                </div>

                {/* In-App toggle */}
                <div className="flex justify-center">
                  <Toggle
                    checked={prefs[inAppKey]}
                    onChange={(v) => update({ [inAppKey]: v })}
                    disabled={inAppDisabled}
                    size="sm"
                  />
                </div>

                {/* Email toggle */}
                <div className="flex justify-center">
                  <Toggle
                    checked={prefs[emailKey]}
                    onChange={(v) => update({ [emailKey]: v })}
                    disabled={emailDisabled}
                    size="sm"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Section 3: Desktop Notifications ──────────────── */}
      <section className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">
          Desktop Notifications
        </h2>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
              <ComputerDesktopIcon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                OS Desktop Alerts
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Native pop-up when a new notification arrives
              </p>
            </div>
          </div>
          <Toggle
            checked={prefs.desktop_enabled}
            onChange={(v) => update({ desktop_enabled: v })}
            disabled={desktopPermission === 'denied' || desktopPermission === 'unsupported'}
          />
        </div>

        {/* Permission status badge */}
        <div className="mt-4 pl-[52px]">
          {desktopPermission === 'unsupported' && (
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Your browser does not support desktop notifications.
            </p>
          )}

          {desktopPermission === 'granted' && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400 px-2.5 py-1 rounded-full">
              <CheckCircleIcon className="w-3.5 h-3.5" /> Permission Granted
            </span>
          )}

          {desktopPermission === 'denied' && (
            <div className="space-y-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-400 px-2.5 py-1 rounded-full">
                <ExclamationTriangleIcon className="w-3.5 h-3.5" /> Permission Denied
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Go to your browser settings → Site Settings → Notifications and allow
                notifications for this site, then reload the page.
              </p>
            </div>
          )}

          {desktopPermission === 'default' && (
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400 px-2.5 py-1 rounded-full">
                Permission not yet granted
              </span>
              <button
                onClick={requestDesktopPermission}
                className="text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 underline underline-offset-2"
              >
                Enable Desktop Alerts
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── Section 4: Quiet Hours ─────────────────────────── */}
      <section className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        <h2 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">
          Quiet Hours
        </h2>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600">
              <MoonIcon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                Enable Quiet Hours
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Suppress desktop and in-app pop-ups during set hours.
                Notifications are still saved to your inbox.
              </p>
            </div>
          </div>
          <Toggle
            checked={prefs.quiet_hours_enabled}
            onChange={(v) => update({ quiet_hours_enabled: v })}
          />
        </div>

        {/* Time pickers — visible only when quiet hours enabled */}
        {prefs.quiet_hours_enabled && (
          <div className="mt-5 pl-[52px] flex flex-wrap items-center gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">
                Start
              </label>
              <input
                type="time"
                value={prefs.quiet_start}
                onChange={(e) => update({ quiet_start: e.target.value })}
                className="
                  px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600
                  text-sm text-gray-900 dark:text-white
                  bg-white dark:bg-gray-700
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                "
              />
            </div>

            <div className="flex items-end pb-2 text-gray-400 dark:text-gray-500 text-sm font-medium">
              to
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">
                End
              </label>
              <input
                type="time"
                value={prefs.quiet_end}
                onChange={(e) => update({ quiet_end: e.target.value })}
                className="
                  px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600
                  text-sm text-gray-900 dark:text-white
                  bg-white dark:bg-gray-700
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                "
              />
            </div>

            <p className="w-full text-xs text-gray-400 dark:text-gray-500 -mt-1">
              Overnight ranges work correctly (e.g. 22:00 → 08:00).
            </p>
          </div>
        )}
      </section>

    </div>
  );
}